import React from 'react';
import { Link } from 'react-router-dom';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { CustomDropdownMenu } from '@/components/ui/CustomDropdownMenu';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { supabase } from '@/integrations/supabase/client';
import ConnectionsIcon from '@/assets/icons/connections.svg';
import { getInitials } from '@/utils/avatarUtils';

interface MyConnectionsListProps {
  connections: any[];
  onConnectionChange?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  isSearching?: boolean;
  totalConnections?: number;
}

export const MyConnectionsList: React.FC<MyConnectionsListProps> = ({ 
  connections, 
  onConnectionChange, 
  hasNextPage, 
  isFetchingNextPage, 
  fetchNextPage,
  isSearching = false,
  totalConnections = 0
}) => {
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const { t } = useI18n();

  async function onRemoveConnection(id: string) {
    setRemovingId(id);
    await supabase.rpc('remove_connection', { target_user: id });
    setRemovingId(null);
    if (onConnectionChange) onConnectionChange();
  }

  if (!connections) return null;
  if (connections.length === 0) {
    // Different empty states based on context
    if (isSearching) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          {/* Empty State Icon */}
          <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
            <img 
              src={ConnectionsIcon} 
              alt="Connections" 
              className="w-10 h-10 text-white/40"
              style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
            />
          </div>
          
          {/* Empty State Title */}
          <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
            {t('connections.no_connections_found_search')}
          </h3>
          
          {/* Empty State Subtitle */}
          <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
            {t('connections.try_search_other_user')}
          </p>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          {/* Empty State Icon */}
          <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
            <img 
              src={ConnectionsIcon} 
              alt="Connections" 
              className="w-10 h-10 text-white/40"
              style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
            />
          </div>
          
          {/* Empty State Title */}
          <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
            {t('connections.no_connections_yet')}
          </h3>
          
          {/* Empty State Subtitle */}
          <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
            {t('connections.when_connect_appear_here')}
          </p>
        </div>
      );
    }
  }

  return (
    <div className="min-w-[320px]">
      {connections.map((profile, idx) => (
        <div
          key={profile.id}
          className="flex justify-between items-start px-0 py-3"
        >
          <div className="flex gap-3 flex-1 items-center">
            <Link to={`/profile/${profile.username}`} className="flex-shrink-0">
              <FramedAvatar src={profile.profile_picture_url} alt={profile.full_name} initials={getInitials(profile.full_name, '')} size={42} frameSize={{ w: 50, h: 55 }} beltRank={profile.belt_rank} username={profile.username} isManual />
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/profile/${profile.username}`} className="font-semibold text-white truncate block hover:underline text-base">
                {profile.full_name}
              </Link>
              <Link to={`/profile/${profile.username}`} className="text-white/60 truncate block hover:underline" style={{ fontSize: '12px' }}>
                @{profile.username}
              </Link>
              {profile.academy && (
                <div className="text-[10px] text-white/60 truncate">{profile.academy}</div>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 ml-2">
            <CustomDropdownMenu
              trigger={
                <span className="p-1 rounded-full focus:outline-none cursor-pointer transition-colors flex items-center justify-center">
                  <MoreHorizontal className="h-4 w-4 text-gray-400" />
                </span>
              }
              items={[{
                label: t('connections.remove_connection'),
                icon: <Trash2 className="h-4 w-4 mr-2" />,
                onClick: () => onRemoveConnection(profile.id),
                className: "text-white"
              }]}
            />
          </div>
        </div>
      ))}

      {/* Load More Button */}
      {hasNextPage && (
        <div className="flex justify-center pt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchNextPage}
            disabled={isFetchingNextPage}
            className="border-white text-white bg-white/5 hover:bg-white/10 rounded-full px-6 font-semibold"
          >
            {isFetchingNextPage ? t('common.loading') : t('connections.view_more_connections')}
          </Button>
        </div>
      )}
    </div>
  );
};